import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ButtonModule, CheckboxModule, InputModule, RadioModule, SelectModule, TextareaModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProductTemperatureDomesticForm, ProductTemperatureDomesticFormGroup } from '../../../models/cod-create.model';
import { AttachmentsInputComponent } from '../../../../shared/components/attachments-input/attachments-input.component';
import { ItemCondition } from '@cod-monorepo/interfaces';
import { ItemConditionMapping } from '../../../../shared/mappings/item-condition.mapping';
import { Store } from '@ngxs/store';
import { filter, tap } from 'rxjs';
import { AddCurrentCodLine, AddErrorMessage, RemoveErrorMessage } from '../../../state/cod.action';
import { WorkflowError } from '../../../../shared/enums/workflow-error';
import { ManagerSelectComponent } from '../../../../shared/components/manager-select/manager-select.component';

@Component({
    selector: 'app-cod-product-temperature-domestic-step',
    templateUrl: './product-temperature-domestic.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        RadioModule,
        CheckboxModule,
        ButtonModule,
        TextareaModule,
        AttachmentsInputComponent,
        ManagerSelectComponent
    ],
    standalone: true
})
export class CodProductTemperatureDomesticStepComponent implements OnInit {
    @Input() productTemperatureDomesticForm: FormGroup<ProductTemperatureDomesticFormGroup> | undefined;

    constructor(private store: Store) { }

    ngOnInit(): void {
        this.initCallTagTrackingNumberTouchedListener();
        this.initRelatedDocumentsListener();
        this.initFinancialAdjustmentChangesListener();
    }

    private relatedCallTrackingNumberTouched = false;
    public itemConditions = ItemCondition;
    public itemConditionMapping = ItemConditionMapping;

    public isDefined(control: keyof ProductTemperatureDomesticForm): boolean {
        return this.productTemperatureDomesticForm?.controls[control].value !== null;
    }

    public isTrue(control: keyof ProductTemperatureDomesticForm): boolean {
        return this.productTemperatureDomesticForm?.controls[control].value === true;
    }

    public isFalse(control: keyof ProductTemperatureDomesticForm): boolean {
        return this.productTemperatureDomesticForm?.controls[control].value === false;
    }

    public itemConditionSelected(condition: ItemCondition): boolean {
        return this.productTemperatureDomesticForm?.controls.itemCondition.value?.includes(condition) ?? false;
    }

    public toggleItemCondition(condition: ItemCondition): void {
        const itemCondition = this.productTemperatureDomesticForm?.controls.itemCondition;

        if (itemCondition) {
            const conditions = itemCondition.value ?? [];

            if (conditions.includes(condition)) {
                itemCondition.setValue(conditions.filter(c => c !== condition));
            } else {
                itemCondition.setValue([...conditions, condition]);
            }
        }
    }

    public handleConfirm() {
        if (this.productTemperatureDomesticForm?.valid) {
            this.store.dispatch(new AddCurrentCodLine());
        }
    }

    public isValid(): boolean {
        return this.productTemperatureDomesticForm?.valid ?? false;
    }

    private initFinancialAdjustmentChangesListener(): void {
        this.productTemperatureDomesticForm?.controls.financialAdjustment.valueChanges
            .pipe(
                filter(value => value === false),
                tap(() => {
                    this.productTemperatureDomesticForm?.controls.adjustmentAmount.setValue(0);
                    this.productTemperatureDomesticForm?.controls.filedWithin30Days.setValue(null);
                    this.productTemperatureDomesticForm?.controls.managerApprovedRequest.setValue(null);
                }))
            .subscribe();
    }

    private initCallTagTrackingNumberTouchedListener(): void {
        this.productTemperatureDomesticForm?.controls.callTagTrackingNumber.valueChanges
            .pipe(
                filter(() => !this.relatedCallTrackingNumberTouched),
                tap(() => {
                    this.store.dispatch(new AddErrorMessage(WorkflowError.UPLOAD_PROOF));

                    this.relatedCallTrackingNumberTouched = true;
                })
            )
            .subscribe();
    }

    private initRelatedDocumentsListener(): void {
        this.productTemperatureDomesticForm?.controls.relatedDocuments.valueChanges
            .pipe(
                filter(value => !!value && value.length > 0),
                tap(() => this.store.dispatch(new RemoveErrorMessage(WorkflowError.UPLOAD_PROOF)))
            )
            .subscribe();
    }
}
